const post = {
  slug: 'database-indexes-that-fix-slow-queries',
  number: 14,
  title: 'The 20% of Database Indexing That Fixes 80% of Slow Queries',
  date: '2024-09-30',
  readTime: '7 min read',
  views: 22,
  category: 'backend',
  tags: ['Backend', 'PostgreSQL', 'Performance'],
  excerpt:
    'Most slow queries I have met were not slow because the database is weak. They were slow because of a missing index or a misunderstood one. A little indexing knowledge goes a very long way.',
  cover: null,
  content: `
A product manager once told me our app was "getting slow," and pointed at a page that took six seconds to load. Everyone assumed we had outgrown the database and needed something bigger. The real cause was a single query doing a full scan of a table that should have been an index lookup. One index later, the page loaded in under a hundred milliseconds, and we did not buy a thing.

That story repeats constantly. Most slow queries I have run into were not slow because the database was weak or the data was too big. They were slow because of a missing index, or an index that existed but was not being used the way the author assumed. You do not need to be a database expert to fix the large majority of these. You need a handful of ideas and the willingness to look at what the database is actually doing.

## An index is a shortcut, and it is not free

Without an index, finding the rows that match a condition means scanning the whole table and checking each row. That is fine for a thousand rows and painful for ten million. An index is a sorted structure that lets the database jump straight to the rows it needs instead of reading everything.

The catch is that indexes cost you on writes. Every insert and update has to keep the index current, and every index takes space. So the goal is not to index everything, which would slow down all your writes for benefits you do not use. The goal is to index the columns you actually filter and sort on, and leave the rest alone.

## Read the query plan instead of guessing

The single most useful habit you can build is to stop guessing why a query is slow and ask the database directly. Every serious database can show you the plan it uses to run a query.

\`\`\`sql
EXPLAIN ANALYZE
SELECT * FROM orders
WHERE customer_id = 42
ORDER BY created_at DESC;
\`\`\`

When the plan shows a sequential scan over a large table where you expected an index lookup, you have found your problem. The plan turns performance work from a mystery into something you can read. I have watched engineers argue for an hour about why a query was slow when the plan would have told them in ten seconds, in plain terms, exactly where the time went.

## Index what you filter and order by

The columns worth indexing are the ones in your WHERE clauses, your JOIN conditions, and your ORDER BY. A column you look records up by constantly, like a foreign key you always join through, is almost always worth an index.

Two refinements carry a lot of weight:

- A composite index across several columns can serve a query that filters on all of them, but the column order matters. Put the column you filter on most, or that narrows the results the most, where it does the most good.
- An index can cover sorting too, so a query that filters and then orders can sometimes be satisfied entirely from one well-chosen index, with no separate sort step at all.

\`\`\`sql
-- Serves "filter by customer, newest first" from a single index
CREATE INDEX idx_orders_customer_created
  ON orders (customer_id, created_at DESC);
\`\`\`

## More indexes is not the answer

Once people learn indexes help reads, the temptation is to add them everywhere, and that backfires. Every extra index slows down writes, takes space, and gives the planner more options to choose between, sometimes badly. Redundant indexes that overlap each other are pure cost with no benefit.

The discipline is to add an index because a specific slow query needs it, confirm with the plan that the query now uses it, and remove indexes that nothing relies on. Targeted beats thorough here, every time.

## The whole loop, and a short checklist

Find the slow query, read its plan, add the index it actually needs, confirm the plan now uses it, and move on. That loop, repeated, resolves the overwhelming majority of database performance problems long before you need anything exotic. Concretely:

- Identify the slow query, do not guess at it from feel.
- Run EXPLAIN ANALYZE and read where the time actually goes.
- Index the columns it filters, joins, and orders on.
- Re-run the plan to confirm the index is being used.
- Periodically remove indexes nothing queries against.

Before you conclude the database is too small, check whether it is simply being asked to read everything when it could be reading almost nothing. Most of the time, that is the entire story.
`,
};

export default post;
